import { Form, FormikProvider, useFormik } from 'formik';
import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router';
import { useHistory } from 'react-router-dom';
import { Box, Flex } from 'reflexbox';

import { useErrorBoundary, useFeature, useLocalize } from '@treams/treams-hooks';
import * as graphql from '@treams/treams-network';
import {
	ActionArchiveListItem,
	ActionListItem,
	Button,
	Container,
	FlexGap,
	GraphQLErrorMessage,
	Jumbo,
	Paragraph,
	SettingsSection,
	Spinner,
	tokens,
	useToasts,
} from '@treams/treams-ui';

import {
	ConversationRevealUploadButton,
	ConversationRevealUploadInfo,
} from '../../conversations/reveal';
import DetailsTableForm from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableForm';
import { DetailsTableFormValueTypes } from '../../packages/kakapo-ui-kit/components/common/DetailsTableForm/DetailsTableFormValue';
import Preview from './components/Preview';

export function useJobTitleOptions() {
	const { localize } = useLocalize();
	const jobTitles = graphql.useJobTitlesQuery({
		variables: { first: 1000, isArchived: false },
	});

	return jobTitles.data?.jobTitles.edges.map((edge) => ({
		label: localize(edge.node.title).text,
		value: edge.node.id,
	}));
}

function EvaluationFormEditor(props: { form: graphql.EvaluationForm }) {
	const { t } = useTranslation();
	const { addToast } = useToasts();

	const history = useHistory();

	const revealEnabled = useFeature(graphql.FeatureName.EvaluationReveal);

	const formik = useFormik({
		initialValues: {
			title: props.form.title,
			description: props.form.description ?? [],
			jobTitleIDs: props.form.jobTitleIDs ?? [],
			useOnlyInCycle: props.form.useOnlyInCycle ?? false,
			showAnswersWhenAllUsersShared: props.form.showAnswersWhenAllUsersShared ?? false,
			sections: props.form.sections.map((section, index) => ({
				id: `__${index}`,
				weight: section.weight ?? undefined,
				items: section.items.slice(),
			})),
		},
		onSubmit: () => {},
		enableReinitialize: true,
	});

	const [error, attempt, reset] = useErrorBoundary();

	const [update, { loading }] = graphql.useUpdateEvaluationFormMutation();
	const [duplicate, { loading: loadingDuplicate }] = graphql.useCreateEvaluationFormMutation();

	const mapInput = (duplicate = false) => {
		return {
			title: formik.values.title.map((text) => ({
				languageID: text.languageID,
				text: duplicate
					? t('common.copyOf', { text: text.text, lng: text.languageID.split('_')[0] })
					: text.text,
			})),
			showAnswersWhenAllUsersShared: formik.values.showAnswersWhenAllUsersShared ?? false,
			description: formik.values.description,
			jobTitleIDs: formik.values.jobTitleIDs.length > 0 ? formik.values.jobTitleIDs : undefined,
			useOnlyInCycle: formik.values.useOnlyInCycle ?? false,
			sections: formik.values.sections.map((section) => ({
				weight: section.weight ?? undefined,
				items: section.items.map((item) => ({
					id: duplicate ? undefined : item.id.startsWith('__') ? undefined : item.id,
					type: item.type,
					fillInBy: item.fillInBy,
					optional: item.optional ?? undefined,
					title: item.title
						? {
								text: item.title.text.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					paragraph: item.paragraph
						? {
								text: item.paragraph.text.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					stars: item.stars
						? {
								title: item.stars.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					openQuestion: item.openQuestion
						? {
								title: item.openQuestion.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					sentiment: item.sentiment
						? {
								title: item.sentiment.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								displayMethod: item.sentiment.displayMethod,
							}
						: undefined,
					multipleChoice: item.multipleChoice
						? {
								title: item.multipleChoice.title.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								optionsMethod: item.multipleChoice.optionsMethod,
								options: item.multipleChoice.options.map((option) => ({
									text: option.text.map((text) => ({
										languageID: text.languageID,
										text: text.text,
									})),
								})),
							}
						: undefined,
					competency: item.competency
						? {
								competencyID: item.competency.competency.id,
								weight: item.competency.weight,
							}
						: undefined,
					finalScore: item.finalScore
						? {
								title: item.finalScore.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
								calculationMethod: item.finalScore.calculationMethod,
								competencyLevelSetID: item.finalScore.competencyLevelSet?.id,
								displayLevelSetID: item.finalScore.displayLevelSet?.id,
								includeReviewGoals: item.finalScore.includeReviewGoals,
								scoreFormat: item.finalScore.scoreFormat,
							}
						: undefined,
					signature: item.signature
						? {
								title: item.signature.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					reviewGoals: item.reviewGoals
						? {
								competencyLevelSetID: item.reviewGoals.competencyLevelSet?.id,
								defaultExplanation: item.reviewGoals.defaultExplanation?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					developmentPlan: item.developmentPlan
						? {
								title: item.developmentPlan.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
					setGoals: item.setGoals
						? {
								title: item.setGoals.title?.map((text) => ({
									languageID: text.languageID,
									text: text.text,
								})),
							}
						: undefined,
				})),
			})),
		};
	};

	const onDuplicate = attempt(async () => {
		const result = await duplicate({
			variables: {
				input: mapInput(true),
			},
		});

		if (result.data) {
			history.push(`/settings/conversations/${result.data.createEvaluationForm.evaluationForm.id}`);
		}
	});

	const onUpdate = attempt(async () => {
		reset();

		await update({
			variables: {
				input: {
					id: props.form.id,
					...mapInput(),
				},
			},
		});

		formik.resetForm({ values: formik.values });

		addToast(t('common.settings.evaluationForms.edit.success'), {
			appearance: 'success',
			autoDismiss: true,
		});
	});

	const options = useJobTitleOptions();

	return (
		<>
			<Prompt
				when={formik.dirty}
				message={t('common.settings.evaluationForms.edit.unsavedChanges')}
			/>

			<FormikProvider value={formik}>
				<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
					<Form>
						<FlexGap flexDirection="column" gap={tokens.gap.section} width="100%">
							<SettingsSection first title={t('common.settings.evaluationForms.edit.details')}>
								<DetailsTableForm
									formValues={[
										{
											label: t('common.settings.evaluationForms.edit.name'),
											name: 'title',
											type: DetailsTableFormValueTypes.Multilingual,
										},
										{
											label: t('common.settings.evaluationForms.edit.description'),
											name: 'description',
											type: DetailsTableFormValueTypes.MultilingualQuill,
										},
										{
											label: t(
												'common.settings.evaluationForms.edit.showAnswersWhenAllUsersShared',
											),
											name: 'showAnswersWhenAllUsersShared',
											type: DetailsTableFormValueTypes.Switch,
										},
									]}
								/>
							</SettingsSection>

							{revealEnabled && (
								<SettingsSection
									title={t('conversations.reveal.settings.title')}
									actions={<ConversationRevealUploadButton evaluationFormID={props.form.id} />}
								>
									<Paragraph mb={0}>{t('conversations.reveal.settings.description')}</Paragraph>
									<ConversationRevealUploadInfo evaluationFormID={props.form.id} />
								</SettingsSection>
							)}

							<SettingsSection title={t('common.settings.evaluationForms.edit.availability')}>
								<DetailsTableForm
									formValues={[
										{
											label: t('common.settings.evaluationForms.edit.jobTitleIDs'),
											name: 'jobTitleIDs',
											type: DetailsTableFormValueTypes.MultiSelect,
											options,
										},
										{
											label: t('common.settings.evaluationForms.edit.useOnlyInCycle'),
											name: 'useOnlyInCycle',
											type: DetailsTableFormValueTypes.Switch,
										},
									]}
								/>
							</SettingsSection>
						</FlexGap>
					</Form>

					<SettingsSection title={t('common.settings.evaluationForms.edit.designer')}>
						<Preview />
					</SettingsSection>

					<GraphQLErrorMessage error={error} showValidationDetails />
					<Flex>
						<Button title={t('common.save')} onClick={onUpdate} loading={loading} />
					</Flex>
					<SettingsSection title={t('common.actions')}>
						<Box width={8 / 12}>
							<ActionArchiveListItem
								archivableItem={props.form}
								archivableItemName={t('common.settings.evaluationForms.archive.module.name')}
								archivableItemKey="evaluationForm"
								useArchive={graphql.useArchiveEvaluationFormMutation}
								useRestore={graphql.useRestoreEvaluationFormMutation}
							/>

							<ActionListItem
								label={t('common.settings.evaluationForms.duplicate.label')}
								buttonTitle={t('common.settings.evaluationForms.duplicate.buttonTitle')}
								onButtonClick={onDuplicate}
								buttonDisabled={loading || loadingDuplicate || formik.dirty}
								disabledText={t('common.settings.evaluationForms.duplicate.disabled')}
							/>
						</Box>
					</SettingsSection>
				</FlexGap>
			</FormikProvider>
		</>
	);
}

function EvaluationFormEditorID(props: { id: string }): ReactElement {
	const { t } = useTranslation();

	const { data, error, loading } = graphql.useEvaluationFormQuery({
		variables: { id: props.id },
		fetchPolicy: 'network-only',
	});

	return (
		<>
			<Jumbo
				title={t('settings.conversations.templates.edit.title')}
				description={t('settings.conversations.templates.edit.description')}
				backButtonLink="/settings/conversations"
			/>
			<Container pt={`${tokens.gap.page}px`} pb={4}>
				{loading && <Spinner centered />}
				{error && <GraphQLErrorMessage error={error.graphQLErrors[0]} />}

				{!loading && !error && (
					<EvaluationFormEditor form={data!.evaluationForm as graphql.EvaluationForm} />
				)}
			</Container>
		</>
	);
}

export default EvaluationFormEditorID;
